import React, { FC, useRef } from 'react';
import { Box, Button, Chip, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getName } from 'country-list';
import CloseIcon from '@mui/icons-material/Close';
import { FiltersData, RangeFilter } from '../../types';

type DatatableChipFiltersProps = {
  filtersData: FiltersData;
  handleDelete: (propertyName: keyof FiltersData) => void;
  handleClearAll: () => void;
  handleCustomChipDelete?: (propertyName: keyof FiltersData, value: string) => void;
};

type FilterItemProp = any;

// FIXME
// type FilterItemProp = [ string, string | string[] | RangeFilter ]

export const DatatableChipFilters: FC<DatatableChipFiltersProps> = ({
  filtersData,
  handleDelete,
  handleCustomChipDelete,
  handleClearAll,
}) => {
  const { t } = useTranslation();
  const chipsCounter = useRef(0);
  const maxCustomChipsCountToDisplay = 6;
  const clearAllButtonChipsMinimalValue = 1;
  const filtersDataArray = filtersData ? Object.entries(filtersData) : [];

  const generateChipName = (item: FilterItemProp) => {
    chipsCounter.current += 1;
    const [name, value] = item;

    const rangeSliderValues = (nameTranslated: string, data: RangeFilter, unit: string = '') => {
      const { fromRange, toRange, emptyValues } = data;
      const isFromRangePresent = fromRange !== null && fromRange !== undefined;
      const range = isFromRangePresent ? ` ${fromRange}${unit} - ${toRange}${unit}` : '';
      const and = isFromRangePresent && emptyValues ? ' and' : '';
      const noValueMsg = emptyValues ? ` Without Values` : '';
      return `${nameTranslated}:${range}${and}${noValueMsg}`;
    };

    if (name === 'maturityDate') {
      const { fromRange } = value as RangeFilter;
      const fromRangeDisplay = fromRange ? ` from ${(fromRange as string).slice(0, 10)}` : ': past';
      return `${t('PORTFOLIO.MATURITY_DATE')}${fromRangeDisplay}`;
    }
    if (name === 'storefrontRatingType' || name === 'ratingType') {
      const storefrontRatingType = t(`PORTFOLIO.${value}`);
      return `${t('PORTFOLIO.RATING_TYPE_ONELINE')}: ${storefrontRatingType}`;
    }
    if (name === 'rating') {
      return rangeSliderValues(t('PORTFOLIO.RATING'), value);
    }
    if (name === 'creditRating') {
      return rangeSliderValues(t('PORTFOLIO.CREDIT_RATING'), value);
    }
    if (name === 'couponPercent') {
      const fromRange = (value as RangeFilter)?.fromRange as number;
      const toRange = (value as RangeFilter)?.toRange as number;
      const emptyValues = value.emptyValues as boolean;
      return rangeSliderValues(
        t('PORTFOLIO.COUPON_RANGE'),
        { fromRange, toRange, emptyValues },
        '%',
      );
    }
    if (name === 'volume') {
      const fromRange = (value as RangeFilter).fromRange?.toLocaleString('en', {
        useGrouping: true,
      });
      const toRange = (value as RangeFilter).toRange?.toLocaleString('en', { useGrouping: true });
      const emptyValues = value.emptyValues as boolean;
      return rangeSliderValues(t('PORTFOLIO.INVESTMENT_VOLUME'), {
        fromRange,
        toRange,
        emptyValues,
      });
    }
    if (name === 'currencyCode') {
      return `${t('PORTFOLIO.CURRENCY')}: ${value}`;
    }
    if (name === 'creditReview') {
      return rangeSliderValues(t('PORTFOLIO.CREDIT_SCORE'), value);
    }
    if (name === 'sigPOD') {
      return rangeSliderValues(t('PORTFOLIO.POD'), value);
    }
    if (name === 'esgEnvironment') {
      return rangeSliderValues(t('PORTFOLIO.ENVIRONMENTAL'), value);
    }
    if (name === 'esgSocial') {
      return rangeSliderValues(t('PORTFOLIO.SOCIAL'), value);
    }
    if (name === 'esgGovernance') {
      return rangeSliderValues(t('PORTFOLIO.GOVERNANCE'), value);
    }
    if (name === 'esgTotal') {
      return rangeSliderValues(t('PORTFOLIO.TOTAL'), value);
    }
    if (name === 'storefrontRatingOutlook') {
      return `${t('PORTFOLIO.OUTLOOK')}`;
    }
    if (name === 'creditRatingShortTermRating') {
      return rangeSliderValues(t('PORTFOLIO.SHORT_TERM_RATINGS'), value);
    }
    if (name === 'sigCountryRiskAssessment') {
      return rangeSliderValues(t('PORTFOLIO.COUNTRY_RISK'), value);
    }

    if (name === 'peergroup') {
      return `${t('PORTFOLIO.FUND_PEERGROUP')}: ${value}`;
    }
    if (name === 'rank') {
      return rangeSliderValues(t('PORTFOLIO.FUND_RANK'), value);
    }
    if (name === 'ratingPoints') {
      return rangeSliderValues(t('PORTFOLIO.FUND_RATING_POINTS'), value);
    }
    if (name === 'returnValue') {
      return rangeSliderValues(t('PORTFOLIO.FUND_RETURN_VALUE'), value);
    }
    if (name === 'risk') {
      return rangeSliderValues(t('PORTFOLIO.FUND_RISK'), value);
    }
    return name;
  };

  const generateCustomChip = (items: FilterItemProp) => {
    const [name, values] = items;
    const tooltipText =
      name === 'countryCode'
        ? values.map((item: string) => getName(item)).join(', ')
        : values.join(', ');
    let chipText: string;
    let render: any;
    let customChipsCounter: number;

    switch (name) {
      case 'countryCode':
        chipText = `Countries: ${values.length}`;
        break;
      case 'industry':
        chipText = `Industries: ${values.length}`;
        break;
      default:
        chipText = `${values.length}`;
    }

    if (values.length < maxCustomChipsCountToDisplay) {
      customChipsCounter = values.length;
      render = values.map((value: string) => (
        <Box p={1} key={value}>
          <Chip
            label={name === 'countryCode' ? getName(value) : value}
            onDelete={() =>
              handleCustomChipDelete && handleCustomChipDelete(name as keyof FiltersData, value)
            }
          />
        </Box>
      ));
    } else {
      customChipsCounter = 1;
      render = (
        <Tooltip title={tooltipText}>
          <Box p={1}>
            <Chip label={chipText} onDelete={() => handleDelete(name as keyof FiltersData)} />
          </Box>
        </Tooltip>
      );
    }
    chipsCounter.current += customChipsCounter;
    return values.length ? render : <></>;
  };

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap">
      {filtersDataArray.map(([name, value]) => {
        return name === 'countryCode' || name === 'industry' ? (
          generateCustomChip([name, value])
        ) : (
          <Box p={1} key={name}>
            <Chip
              label={generateChipName([name, value])}
              onDelete={() => {
                chipsCounter.current -= 1;
                handleDelete(name as keyof FiltersData);
              }}
            />
          </Box>
        );
      })}
      {chipsCounter.current > clearAllButtonChipsMinimalValue ? (
        <Box p={1}>
          <Button
            onClick={() => {
              chipsCounter.current = 0;
              handleClearAll();
            }}
          >
            <Box display="flex">
              Clear all
              <CloseIcon />
            </Box>
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};
